import { defineStore } from 'pinia'

import {
    getDetailVoipShield,
    updateVoipShield,
    getAvailableCampaigns,
    attachCampaign,
    detachCampaign,
    removeCarrier,
    addCarrier,
    updateCarrier,
} from '@/api/call-tracking/suppression-list/voip-shield-routes'

import {
    getFilterOnPageInLocalStorageByWorkspaceId,
    setFilterOnPageInLocalStorageByWorkspaceId,
} from '@/helpers/save-sorting-local-storage-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { usePermissionsStore } from '@/store/permissionsStore'

import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'

export const useDetailVoipShieldStore = defineStore('detailVoipShield', {
    state: () => ({
        loading: false,
        actionLoading: false,

        slug: null,

        formData: {
            name: null,
        },
        campaigns: [],
        campaignsAvailable: [],
        carriers: [],
        carriersSelected: [],

        onPage: 25,
    }),

    actions: {
        setVoipShieldSlug(slug) {
            this.slug = slug
        },

        changeOnPage(onPage) {
            this.onPage = onPage

            setFilterOnPageInLocalStorageByWorkspaceId(onPage, SETTINGS_HEADER_TABLE.blockedCarrier)
        },

        getLocalStorageOnPage() {
            const savedOnPage = getFilterOnPageInLocalStorageByWorkspaceId(SETTINGS_HEADER_TABLE.blockedCarrier)

            this.onPage = savedOnPage ? Number(savedOnPage) : 25
        },

        _parseProps({ name, campaigns = [], carriers = [] }) {
            this.formData = {
                name,
            }
            this.campaigns = campaigns
            this.carriers = carriers
        },

        _changeCampaignsAfterDetach({ slug }) {
            const findIndex = this.campaigns.findIndex((item) => item.slug === slug)

            if (findIndex !== -1) {
                this.campaigns.splice(findIndex, 1)
            }
        },

        _changeCarrierAfterDetach(slugs) {
            this.carriers = this.carriers.filter((item) => !slugs.includes(item.slug))
        },

        _changeCarriersAfterUpdate(payload) {
            const findIndex = this.carriers.findIndex((item) => item.slug === payload.slug)

            if (findIndex !== -1) {
                this.carriers.splice(findIndex, 1, payload)
            }
        },

        _changeCampaignsAvailableAfterAttach({ slug }) {
            const findIndex = this.campaignsAvailable.findIndex((item) => item.slug === slug)

            if (findIndex !== -1) {
                this.campaignsAvailable.splice(findIndex, 1)
            }
        },

        _changeCampaignsAvailableAfterDetach(payload) {
            this.campaignsAvailable.push(payload)
        },

        changeSelectedItems(newSelectedItems) {
            this.carriersSelected = newSelectedItems
        },

        clearSelected() {
            this.carriersSelected = []
        },

        async goToLoadDetailVoipShield() {
            this.loading = true

            const { success, payload, message } = await getDetailVoipShield(this.slug)

            if (success) {
                this._parseProps(payload)
                this.goToLoadAvailableCampaigns()
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return success
        },

        async goToUpdateVoipShield(formDataForUpdate) {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await updateVoipShield({
                formData: formDataForUpdate,
                slug: this.slug,
            })

            if (success) {
                this._parseProps({ ...payload, campaigns: this.campaigns, carriers: this.carriers })

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToLoadAvailableCampaigns() {
            const permissionsStore = usePermissionsStore()

            if (!permissionsStore.permissions.campaigns.view || !permissionsStore.permissions.voipShield.edit) {
                return
            }

            const { success, payload, message } = await getAvailableCampaigns(this.slug)

            if (success) {
                this.campaignsAvailable = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToAttachCampaign(campaign) {
            const defaultStore = useDefaultStore()

            const { success, message } = await attachCampaign({
                slug: this.slug,
                campaign: campaign.slug,
            })

            if (success) {
                this.campaigns.push(campaign)
                this._changeCampaignsAvailableAfterAttach(campaign)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToDetachCampaign(payload) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await detachCampaign({
                slug: this.slug,
                campaign: payload.slug,
            })

            if (success) {
                this._changeCampaignsAfterDetach(payload)
                this._changeCampaignsAvailableAfterDetach(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToUnblockCarrier(slugs) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await removeCarrier({
                slug: this.slug,
                data: { slugs },
            })

            if (success) {
                this._changeCarrierAfterDetach(slugs)
                this.clearSelected()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToAddCarrier(formData) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, payload, message } = await addCarrier({
                slug: this.slug,
                formData,
            })

            if (success) {
                this.carriers = [...this.carriers, payload]
                this.clearSelected()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToUpdateCarrier({ slugCarrier, formData }) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, payload, message } = await updateCarrier({
                slug: this.slug,
                slugCarrier,
                formData,
            })

            if (success) {
                this._changeCarriersAfterUpdate(payload)
                this.clearSelected()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },
    },
})
